import { useMemo, useState } from 'react';
import { Stack } from '@chakra-ui/react';
import UserFilters from '../UserFilters/UserFilters';
import UserTable from '../UserTable/UserTable';
import EmptyState from 'components/EmptyState/EmptyState';
import PaginationFooter from 'components/Pagination/PaginationFooter/PaginationFooter';
import { PaginationModel, UserModel } from 'models';
import { UserAccountStatus, UserRole } from 'models/User/User';


export interface UserGridProps {
  paginatedUsers: PaginationModel<UserModel>;
  isLoading: boolean;
  page: number;
  onPageChange: (page: number) => void;
}

export default function UserGrid({ paginatedUsers, isLoading, page, onPageChange }: UserGridProps) {
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<UserAccountStatus | undefined>(undefined);
  const [roleFilter, setRoleFilter] = useState<UserRole | undefined>(undefined);

  const filteredUsers = useMemo(() => {
    const users = paginatedUsers?.data || [];
    let result = [...users];

    const searchTerm = query.trim().toLowerCase();
    if (searchTerm.length) {
      result = result.filter(user => {
        const email = user.email?.toLowerCase() || '';
        const username = user.userName?.toLowerCase() || '';
        const name = user.name?.toLowerCase() || '';
        return email.includes(searchTerm) || username.includes(searchTerm) || name.includes(searchTerm);
      });
    }

    if (statusFilter) {
      result = result.filter(user => user.accountStatus === statusFilter);
    }

    if (roleFilter) {
      result = result.filter(user => user.roles?.includes(roleFilter));
    }

    return result;
  }, [paginatedUsers, query, statusFilter, roleFilter]);

  const totalPages = paginatedUsers?.totalPages || 1;

  const handleQueryChange = (value: string) => {
    setQuery(value);
    onPageChange(1);
  };

  const handleStatusChange = (value?: UserAccountStatus) => {
    setStatusFilter(value);
    onPageChange(1);
  };

  const handleRoleChange = (value?: UserRole) => {
    setRoleFilter(value);
    onPageChange(1);
  };

  const handleClearFilters = () => {
    setQuery('');
    setStatusFilter(undefined);
    setRoleFilter(undefined);
    onPageChange(1);
  };

  const isOnFilterMode = (): boolean => {
    return query.length > 0 || statusFilter !== undefined || roleFilter !== undefined;
  };

  const renderContent = () => {
    if (isLoading) {
      return <UserTable users={[]} isLoading={true} />;
    }

    if (!filteredUsers.length) {
      return (
        <EmptyState
          header="No Users"
          description="No Results Found"
          showButton={isOnFilterMode()}
          onButtonClick={handleClearFilters}
          buttonText="Clear Filters"
        />
      );
    }

    return <UserTable users={filteredUsers} isLoading={false} />;
  };

  return (
    <Stack spacing={6}>
      <UserFilters
        defaultQuery={query}
        defaultStatus={statusFilter}
        defaultRole={roleFilter}
        onQueryChange={handleQueryChange}
        onStatusChange={handleStatusChange}
        onRoleChange={handleRoleChange}
      />
      {renderContent()}
      {totalPages > 1 && filteredUsers.length > 0 && (
        <PaginationFooter
          currentPage={page}
          lastPage={totalPages}
          siblingsCount={1}
          onPageChange={onPageChange}
        />
      )}
    </Stack>
  );
}
